import React from 'react';
import { bool, func, string } from 'prop-types';
import css from './AdminSocialPostsPage.css';

/**
 * Reusable on-brand switch — NEVER colour-alone: a text state label always sits
 * beside the track, and `role="switch"` + `aria-checked`/`aria-busy` expose the
 * state and any in-flight toggle to assistive tech. Reduced-motion is handled in
 * CSS. `tone` picks the on-colour:
 *   - 'success' → forward-action green (capability on/off toggles)
 *   - 'neutral' → brand navy ink (either/or modes where green would be misread
 *                 as "go / live")
 *
 * `stopPropagation` keeps the toggle from bubbling to a clickable row wrapper
 * (used by the workflow list rows).
 */
const Switch = ({
    checked,
    onChange,
    onLabel,
    offLabel,
    tone,
    busy,
    disabled,
    ariaLabel,
    stopPropagation,
}) => {
    const toneClass = tone === 'neutral' ? css.switchNeutral : css.switchSuccess;
    const blocked = busy || disabled;
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-busy={busy || undefined}
            aria-label={ariaLabel}
            disabled={disabled || undefined}
            className={`${css.switch} ${toneClass} ${checked ? css.switchOn : ''} ${
                busy ? css.switchBusy : ''
            }`}
            onClick={e => {
                if (stopPropagation) e.stopPropagation();
                if (!blocked) onChange(!checked);
            }}
            onKeyDown={stopPropagation ? e => e.stopPropagation() : undefined}
        >
            <span className={css.switchTrack} aria-hidden="true">
                <span className={css.switchThumb} />
            </span>
            <span className={css.switchLabel}>{checked ? onLabel : offLabel}</span>
        </button>
    );
};

Switch.defaultProps = {
    onLabel: 'An',
    offLabel: 'Aus',
    tone: 'success',
    busy: false,
    disabled: false,
    ariaLabel: undefined,
    stopPropagation: false,
};

Switch.propTypes = {
    checked: bool.isRequired,
    onChange: func.isRequired,
    onLabel: string,
    offLabel: string,
    tone: string,
    busy: bool,
    disabled: bool,
    ariaLabel: string,
    stopPropagation: bool,
};

export default Switch;
